import { CAMERA_CONFIG } from './cameraConfig';

const MAX_NODE_WIDTH = CAMERA_CONFIG.MAX_NODE_WIDTH;
const MIN_NODE_SPACING = CAMERA_CONFIG.MIN_NODE_SPACING;
const MIN_VERTICAL_SPACING = CAMERA_CONFIG.MIN_VERTICAL_SPACING;
const LEVEL_HEIGHT = CAMERA_CONFIG.LEVEL_HEIGHT;
const SUBTREE_PADDING = CAMERA_CONFIG.SUBTREE_PADDING;

/**
 * Get all descendants of a node
 */
export const getDescendants = (allNodes, parentId) => {
  const children = allNodes.filter(node => node.parentId === parentId);
  let descendants = [...children];

  children.forEach(child => {
    descendants = descendants.concat(getDescendants(allNodes, child.id));
  });

  return descendants;
};

/**
 * Calculate the maximum number of leaves per level for a subtree
 */
export const calculateMaxLeavesPerLevel = (nodeId, allNodes, level = 0) => {
  const children = allNodes.filter(node => node.parentId === nodeId);

  if (children.length === 0) {
    return 1; // Leaf node - counts as 1
  }

  // For each child, recursively calculate max nodes
  const childMaxes = children.map(child =>
    calculateMaxLeavesPerLevel(child.id, allNodes, level + 1)
  );

  // Number of nodes at current level
  const nodesAtThisLevel = children.length;

  // Maximum number of nodes in child levels
  const maxInChildLevels = childMaxes.length > 0 ? Math.max(...childMaxes) : 0;

  // Total sum of all children (to calculate spread)
  const totalChildrenWidth = childMaxes.reduce((sum, max) => sum + max, 0);

  // Return the maximum between:
  // - number of nodes at current level
  // - maximum found in child levels
  // - total sum of children widths
  return Math.max(nodesAtThisLevel, maxInChildLevels, totalChildrenWidth);
};

/**
 * Calculate the height of a tree from a node
 */
export const calculateTreeHeight = (nodeId, allNodes, level = 0) => {
  const children = allNodes.filter(node => node.parentId === nodeId);

  if (children.length === 0) {
    return level + 1; // Leaf node height
  }

  // Calculate max height among all children
  const maxChildHeight = Math.max(...children.map(child =>
    calculateTreeHeight(child.id, allNodes, level + 1)
  ));

  return Math.max(level + 1, maxChildHeight);
};

/**
 * Calculate the depth of a tree from a node
 */
export const calculateTreeDepth = (nodeId, allNodes) => {
  const children = allNodes.filter(node => node.parentId === nodeId);

  if (children.length === 0) {
    return 1; // Leaf node has depth 1
  }

  // Calculate depth recursively for each child and take the maximum
  const childDepths = children.map(child => calculateTreeDepth(child.id, allNodes));
  return 1 + Math.max(...childDepths);
};

/**
 * Calculate the width required for a subtree
 */
export const calculateSubtreeWidth = (nodeId, allNodes) => {
  const children = allNodes.filter(node => node.parentId === nodeId);

  if (children.length === 0) {
    // Leaf node - width of one node
    return MAX_NODE_WIDTH;
  }

  // Calculate total width needed for all children
  let totalWidth = 0;
  children.forEach((child, index) => {
    const childSubtreeWidth = calculateSubtreeWidth(child.id, allNodes);
    totalWidth += childSubtreeWidth;

    // Add minimum spacing between siblings
    if (index < children.length - 1) {
      totalWidth += MIN_NODE_SPACING;
    }
  });

  // Calculate maximum number of nodes in all descendant levels
  const maxNodesInDescendants = calculateMaxLeavesPerLevel(nodeId, allNodes);

  // Width needed based on maximum number of nodes
  const widthBasedOnMaxNodes = maxNodesInDescendants * (MAX_NODE_WIDTH + MIN_NODE_SPACING);

  // Return the maximum between:
  // - recursively calculated width
  // - width based on maximum number of nodes
  // - minimum width
  return Math.max(totalWidth, widthBasedOnMaxNodes, MAX_NODE_WIDTH);
};

/**
 * Calculate the width of an entire tree
 */
export const calculateTreeWidth = (tree) => {
  return calculateSubtreeWidth(tree.root.id, tree.allNodes);
};

/**
 * Build separate trees from root nodes
 */
export const buildSeparateTrees = (allNodes, rootNodes) => {
  const trees = [];

  rootNodes.forEach(rootNode => {
    // Get all descendants of this root
    const treeNodes = getDescendants(allNodes, rootNode.id);
    treeNodes.unshift(rootNode); // Add root to the beginning

    // Group by levels within this tree
    const nodesByLevel = new Map();
    treeNodes.forEach(node => {
      const level = node.level || 0;
      if (!nodesByLevel.has(level)) {
        nodesByLevel.set(level, []);
      }
      nodesByLevel.get(level).push(node);
    });

    trees.push({
      root: rootNode,
      allNodes: treeNodes,
      nodesByLevel: nodesByLevel,
      levels: Array.from(nodesByLevel.keys()).sort((a, b) => a - b)
    });
  });

  return trees;
};

/**
 * Layout a single subtree recursively
 */
export const layoutSubtree = (parentId, allNodes, positions, parentX, parentY, availableWidth) => {
  const children = allNodes.filter(node => node.parentId === parentId);

  if (children.length === 0) {
    return; // No children to position
  }

  const childY = parentY + LEVEL_HEIGHT + MIN_VERTICAL_SPACING;

  if (children.length === 1) {
    // Single child - center under parent
    const child = children[0];
    positions.set(child.id, { x: parentX, y: childY });

    // Recursively layout this child's subtree
    const childSubtreeWidth = calculateSubtreeWidth(child.id, allNodes);
    layoutSubtree(child.id, allNodes, positions, parentX, childY, childSubtreeWidth);
  } else {
    // Multiple children - distribute based on their subtree widths
    const childSubtreeWidths = children.map(child =>
      calculateSubtreeWidth(child.id, allNodes)
    );

    // Calculate total width needed including minimum spacing
    const totalChildrenWidth = childSubtreeWidths.reduce((sum, width) => sum + width, 0) +
      (children.length - 1) * MIN_NODE_SPACING;

    // Start from the left edge of available width
    const startX = parentX - (totalChildrenWidth / 2);
    let currentX = startX;

    children.forEach((child, index) => {
      const childSubtreeWidth = childSubtreeWidths[index];
      const childCenterX = currentX + (childSubtreeWidth / 2);

      // Position this child
      positions.set(child.id, { x: childCenterX, y: childY });

      // Recursively layout this child's subtree within its allocated space
      layoutSubtree(child.id, allNodes, positions, childCenterX, childY, childSubtreeWidth);

      // Move to next child position with minimum spacing
      currentX += childSubtreeWidth + MIN_NODE_SPACING;
    });
  }
};

/**
 * Layout a single tree in a grid position
 */
export const layoutSingleTreeInGrid = (tree, startX, startY, treeWidth) => {
  const positions = new Map();
  const treeCenterX = startX + (treeWidth / 2);

  // Position root at center of tree area with custom Y position
  positions.set(tree.root.id, { x: treeCenterX, y: startY });

  // Recursively position all subtrees
  layoutSubtree(tree.root.id, tree.allNodes, positions, treeCenterX, startY, treeWidth);

  return positions;
};

/**
 * Calculate node positions for all trees
 */
export const calculateNodePositions = (allNodes) => {
  if (!allNodes || allNodes.length === 0) return { positions: new Map(), treeBounds: [] };

  const positions = new Map();

  // Find all root nodes (nodes without parents or with level 0/1)
  const rootNodes = allNodes.filter(node =>
    !node.parentId ||
    node.level === 0 ||
    (node.level === 1 && !allNodes.some(n => n.id === node.parentId))
  );

  // Group nodes into separate trees
  const trees = buildSeparateTrees(allNodes, rootNodes);

  // Calculate layout for each tree separately
  const treeBounds = [];
  const treesWithWidth = trees.map(tree => ({
    ...tree,
    width: calculateTreeWidth(tree)
  }));

  // Calculate grid dimensions - more compact layout
  const numTrees = treesWithWidth.length;
  const avgTreeSize = treesWithWidth.reduce((sum, tree) => sum + tree.width, 0) / numTrees;
  const sizeFactor = avgTreeSize > 2000 ? 0.8 : 1.8;
  const colsPerRow = Math.max(2, Math.ceil(Math.sqrt(numTrees) * sizeFactor));

  // Calculate actual tree dimensions based on leaves per level and height
  const treesWithDimensions = treesWithWidth.map(tree => {
    const maxLeavesPerLevel = calculateMaxLeavesPerLevel(tree.root.id, tree.allNodes);
    const treeHeight = calculateTreeHeight(tree.root.id, tree.allNodes);
    const treeDepth = calculateTreeDepth(tree.root.id, tree.allNodes);

    // Dynamic width based on max leaves per level
    const leafBasedWidth = maxLeavesPerLevel * (MAX_NODE_WIDTH + MIN_NODE_SPACING);
    // Dynamic height based on actual tree height
    const heightBasedHeight = treeHeight * (LEVEL_HEIGHT + MIN_VERTICAL_SPACING);

    const adjustedWidth = Math.max(tree.width, leafBasedWidth);
    const adjustedHeight = Math.max(heightBasedHeight, 400); // Minimum height

    return {
      ...tree,
      width: adjustedWidth,
      height: adjustedHeight,
      maxLeavesPerLevel,
      treeHeight,
      depth: treeDepth
    };
  });

  // Calculate grid spacing based on tree complexity and dimensions
  const maxTreeWidth = Math.max(...treesWithDimensions.map(t => t.width));
  const maxTreeHeight = Math.max(...treesWithDimensions.map(t => t.height));
  const maxLeavesAcrossAllTrees = Math.max(...treesWithDimensions.map(t => t.maxLeavesPerLevel));
  const maxHeightAcrossAllTrees = Math.max(...treesWithDimensions.map(t => t.treeHeight));

  // Dynamic spacing based on tree complexity
  const complexityFactor = Math.max(maxLeavesAcrossAllTrees / 3, maxHeightAcrossAllTrees / 3, 1);
  const gridColSpacing = maxTreeWidth * (1.2 + complexityFactor * 0.1);
  const gridRowSpacing = maxTreeHeight * (1.1 + complexityFactor * 0.05);

  treesWithDimensions.forEach((tree, treeIndex) => {
    // Calculate grid position
    const row = Math.floor(treeIndex / colsPerRow);
    const col = treeIndex % colsPerRow;

    // Calculate position in grid
    const treeStartX = SUBTREE_PADDING + (col * gridColSpacing);
    const treeStartY = 400 + (row * gridRowSpacing);

    // Position this tree
    const treePositions = layoutSingleTreeInGrid(tree, treeStartX, treeStartY, tree.width);

    // Add tree positions to global positions
    treePositions.forEach((pos, nodeId) => {
      positions.set(nodeId, pos);
    });

    // Store tree bounds for labels
    treeBounds.push({
      root: tree.root,
      startX: treeStartX,
      centerX: treeStartX + (tree.width / 2),
      width: tree.width,
      startY: treeStartY
    });
  });

  return { positions, treeBounds };
};
